import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CreditCard, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ClientSidebar from '../Sidebar/ClientSidebar';
import { parseUTC } from '../../lib/date';

const MyPayments = ({ user }) => {
  const { toast } = useToast();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0 });

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      // Load both orders and consultings
      const [ordersRes, consultingsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consulting/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const ordersData = await ordersRes.json();
      const consultingsData = await consultingsRes.json();

      // Merge and format payments
      const allPayments = [
        ...ordersData.map(order => ({
          id: order.order_id,
          description: `Tuning Files - ${order.vehicle_info.brand} ${order.vehicle_info.model}`,
          date: order.created_at,
          amount: order.price,
          status: order.payment_status,
          type: 'tuning'
        })),
        ...consultingsData.map(consulting => ({
          id: consulting.consulting_id,
          description: `Consulting - ${consulting.vehicle_info.brand} ${consulting.vehicle_info.model}`,
          date: consulting.created_at,
          amount: consulting.price,
          status: consulting.payment_status,
          type: 'consulting'
        }))
      ];

      // Sort by date (most recent first)
      allPayments.sort((a, b) => parseUTC(b.date) - parseUTC(a.date));

      setPayments(allPayments);

      // Calculate stats
      const totalPaid = allPayments
        .filter(p => p.status === 'paid')
        .reduce((sum, p) => sum + p.amount, 0);
      
      const paidCount = allPayments.filter(p => p.status === 'paid').length;
      const pendingCount = allPayments.filter(p => p.status === 'pending').length;

      setStats({
        total: totalPaid,
        paid: paidCount,
        pending: pendingCount
      });

    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger l'historique des paiements",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Payé</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
      case 'refunded':
        return <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30"><XCircle className="h-3 w-3 mr-1" />Remboursé</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30">Inconnu</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              Mes <span className="text-[#C00000]">Paiements</span>
            </h1>
            <p className="text-[#A6A6A6]">Historique de vos transactions</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm">Total dépensé</p>
                    <p className="text-2xl font-bold text-white">{stats.total}€</p>
                  </div>
                  <CreditCard className="h-8 w-8 text-[#C00000]" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm">Paiements réussis</p>
                    <p className="text-2xl font-bold text-green-500">{stats.paid}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm">En attente</p>
                    <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
                  </div>
                  <Clock className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payments List */}
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
            <CardHeader>
              <CardTitle className="text-white">Historique des transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <p className="text-[#A6A6A6]">Chargement...</p>
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="h-12 w-12 text-[#A6A6A6] mx-auto mb-4" />
                  <p className="text-[#A6A6A6]">Aucune transaction pour le moment</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-4 bg-[#0a0a0a] rounded-lg">
                      <div className="flex-1">
                        <p className="text-white font-semibold">{payment.description}</p>
                        <p className="text-[#A6A6A6] text-sm">{parseUTC(payment.date).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-white font-bold text-lg">{payment.amount}€</span>
                        {getPaymentStatusBadge(payment.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MyPayments;